import { useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useReactToPrint } from 'react-to-print';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { addDays } from 'date-fns';
import { ArrowLeft, CircleUser, Nfc, TriangleAlert, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { api } from '@/lib/api.ts';
import { SITE_NAME } from '@/lib/route-meta.ts';
import { useCurrentUser } from '@/hooks/queries/auth.ts';
import { rfidConflictUsername } from '@/hooks/queries/rfids.ts';
import type { APICreatedUser } from '@/types/index.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker, toDateValue } from '@/components/date-picker.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx';
import { PageContainer } from '@/components/page-container.tsx';
import { PasswordInput } from '@/components/password-input';

export const Route = createFileRoute('/_logged/users/create')({
  component: CreateUserPage,
  staticData: {
    label: msg({
      comment: 'Route label for the user-creation page (breadcrumb and document title)',
      message: 'New user',
    }),
  },
});

type CreateUserBody = {
  authorized_cs: Array<number>;
  username?: string;
  new_password?: string;
  email?: string;
  scheduled_expire_time?: string;
};

type NrgkickBody = { device_serial: string; price_per_kwh: number };

type FollowUpFailures = {
  nrgkick: boolean;
  rfids: Array<{ uid: string; conflictName: string | null }>;
};

const NO_FAILURES: FollowUpFailures = { nrgkick: false, rfids: [] };

const RFID_UID_PATTERN = /^[0-9A-F]{4,16}$/;

function isFutureDate(value: string): boolean {
  return new Date(value).getTime() > Date.now();
}

/**
 * The post-creation follow-up calls (decision 23): NRGKick enable and one RFID
 * assignment per collected UID, all fired concurrently and independently so one
 * failure never blocks the others. Never rejects — the resolved value is the
 * failure report the credentials view renders as its warning block.
 */
async function runFollowUps(
  userId: number,
  nrgkickBody: NrgkickBody | null,
  rfidUids: Array<string>,
): Promise<FollowUpFailures> {
  const [nrgkick, rfidResults] = await Promise.all([
    (async () => {
      if (nrgkickBody === null) return false;
      try {
        await api.post(`parkadmin/current/users/${userId}/nrgkick_enable/`, nrgkickBody);
        return false;
      } catch {
        return true;
      }
    })(),
    Promise.all(
      rfidUids.map(async (uid) => {
        try {
          await api.post(`parkadmin/current/users/${userId}/rfids/`, { rfid_uid: uid });
          return null;
        } catch (error) {
          return { uid, conflictName: rfidConflictUsername(error) };
        }
      }),
    ),
  ]);
  return { nrgkick, rfids: rfidResults.filter((failure) => failure !== null) };
}

/**
 * Full-page user creation — the fast-create dialog's ergonomics (placeholder
 * auto-username, tomorrow as the first selectable expiration, in-place swap to
 * the printable credentials view) combined with the edit page's feature
 * sections, in one creation gesture (decision 23). One form, one submit:
 * `POST parkadmin/current/users/` creates the account, then the NRGKick enable
 * and RFID assignments are chained with the returned id. The credentials view
 * is always shown once the account exists — the generated password lives only
 * in that response — so follow-up failures surface as a warning block inside
 * it, with the edit page as the fix path. Deliberately NOT shared with the
 * fast-create dialog (decision 9), which never gets the feature sections
 * (decision 21). Account-level — independent of the selected park.
 */
function CreateUserPage() {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const hasNrgkick = currentUser.features.parkadmin?.feat_nrgkick ?? false;
  const hasRfid = currentUser.features.parkadmin?.feat_rfid ?? false;

  const [noExpiration, setNoExpiration] = useState(true);
  const [nrgkickOn, setNrgkickOn] = useState(false);
  const [rfidUids, setRfidUids] = useState<Array<string>>([]);
  const [rfidDraft, setRfidDraft] = useState('');
  const [rfidDraftError, setRfidDraftError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<APICreatedUser | null>(null);
  const [failures, setFailures] = useState<FollowUpFailures>(NO_FAILURES);

  const createUserSchema = z
    .object({
      username: z.union([
        z.literal(''),
        z
          .string()
          .min(5, t`At least 5 characters`)
          .max(128, t`Too long`)
          .regex(/^[a-zA-Z0-9_-]+$/, t`Letters, numbers, - and _ only`),
      ]),
      newPassword: z.union([
        z.literal(''),
        z
          .string()
          .min(4, t`Must be at least 4 characters`)
          .max(128, t`Password is too long`),
      ]),
      confirmPassword: z.string(),
      email: z.union([z.literal(''), z.email(t`Enter a valid email address`)]),
      expiresOn: z.union([z.literal(''), z.string().refine(isFutureDate, t`Pick a future date`)]),
      // The NRGKick constraints apply only while the switch is on — off, the
      // fields are hidden and never submitted.
      nrgkickDeviceSerial: nrgkickOn ? z.string().min(1, t`Required`) : z.string(),
      nrgkickPricePerKwh:
        nrgkickOn ?
          z.string().refine((value) => Number(value) > 0, t`Must be greater than 0`)
        : z.string(),
    })
    .refine((values) => values.newPassword === values.confirmPassword, {
      message: t`Passwords do not match`,
      path: ['confirmPassword'],
    });

  const mutation = useMutation({
    mutationFn: async (body: CreateUserBody) => {
      const { data } = await api.post<APICreatedUser>('parkadmin/current/users/', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const form = useForm({
    defaultValues: {
      username: '',
      newPassword: '',
      confirmPassword: '',
      email: '',
      expiresOn: '',
      nrgkickDeviceSerial: '',
      nrgkickPricePerKwh: '0.50',
    },
    validators: { onSubmit: createUserSchema },
    onSubmit: async ({ value }) => {
      // A typed-but-not-added card UID must never be silently dropped.
      if (rfidDraft !== '') {
        setRfidDraftError(t`Add the card or clear the field first.`);
        return;
      }
      const body: CreateUserBody = { authorized_cs: [] };
      if (value.username !== '') body.username = value.username;
      if (value.newPassword !== '') body.new_password = value.newPassword;
      if (value.email !== '') body.email = value.email;
      if (!noExpiration && value.expiresOn !== '') {
        body.scheduled_expire_time = new Date(value.expiresOn).toISOString();
      }
      let created: APICreatedUser;
      try {
        created = await mutation.mutateAsync(body);
      } catch {
        toast.error(t`Could not create the user. The username may already be taken.`);
        return;
      }
      // The backend returns `password: null` when one was set explicitly — the
      // credentials view still shows the chosen value, from the form.
      if (created.password === null && value.newPassword !== '') {
        created = { ...created, password: value.newPassword };
      }
      const nrgkickBody =
        nrgkickOn ?
          {
            device_serial: value.nrgkickDeviceSerial,
            price_per_kwh: Math.floor(Number(value.nrgkickPricePerKwh) * 100),
          }
        : null;
      setFailures(await runFollowUps(created.id, nrgkickBody, rfidUids));
      setCredentials(created);
      resetForm();
    },
  });

  function resetForm() {
    form.reset();
    setNoExpiration(true);
    setNrgkickOn(false);
    setRfidUids([]);
    setRfidDraft('');
    setRfidDraftError(null);
  }

  function handleRfidDraftChange(raw: string) {
    setRfidDraft(raw.replace(/[^0-9a-fA-F]/g, '').toUpperCase());
    setRfidDraftError(null);
  }

  function handleAddRfid() {
    if (!RFID_UID_PATTERN.test(rfidDraft)) {
      setRfidDraftError(t`Use 4 to 16 hex characters (0-9, A-F)`);
      return;
    }
    if (rfidUids.includes(rfidDraft)) {
      setRfidDraftError(
        t({
          comment:
            'Error under the Card UID input on the user-creation page when the entered RFID UID is already in the list of cards to assign',
          message: 'This card is already in the list.',
        }),
      );
      return;
    }
    setRfidUids([...rfidUids, rfidDraft]);
    setRfidDraft('');
  }

  return (
    <PageContainer>
      {credentials === null ?
        <>
          <header className='flex items-center gap-4'>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant='outline'
                    size='icon'
                    nativeButton={false}
                    aria-label={t`Back to users`}
                    render={<Link to='/users' />}
                  >
                    <ArrowLeft />
                  </Button>
                }
              />
              <TooltipContent>
                <Trans>Back to users</Trans>
              </TooltipContent>
            </Tooltip>
            <h1 className='text-3xl font-semibold tracking-tight'>
              <Trans>New user</Trans>
            </h1>
          </header>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <div className='grid gap-6 md:grid-cols-2'>
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <CircleUser className='size-5' />
                    <Trans comment='Title of the card on the user edit page holding the account fields (email, password, expiration).'>
                      Account
                    </Trans>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FieldGroup>
                    <form.Field name='username'>
                      {(field) => (
                        <Field data-invalid={field.state.meta.errors.length > 0}>
                          <FieldLabel htmlFor={field.name}>
                            <Trans>Username</Trans>
                          </FieldLabel>
                          <Input
                            id={field.name}
                            name={field.name}
                            autoComplete='off'
                            placeholder={t({
                              comment:
                                'Placeholder of the username field in the user-creation form; leaving the field empty makes the server generate a username',
                              message: 'Automatic username',
                            })}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(event) => field.handleChange(event.target.value)}
                            aria-invalid={field.state.meta.errors.length > 0}
                          />
                          <FieldError errors={field.state.meta.errors} />
                        </Field>
                      )}
                    </form.Field>

                    <form.Field name='newPassword'>
                      {(field) => (
                        <Field data-invalid={field.state.meta.errors.length > 0}>
                          <FieldLabel htmlFor={field.name}>
                            <Trans>Password</Trans>
                          </FieldLabel>
                          <PasswordInput
                            id={field.name}
                            name={field.name}
                            autoComplete='new-password'
                            placeholder={t({
                              comment:
                                'Placeholder of the password field in the user-creation form; leaving the field empty makes the server generate a password',
                              message: 'Automatic password',
                            })}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(event) => {
                              field.handleChange(event.target.value);
                              // Blank password = autogenerate: the confirm field
                              // goes disabled, so clear it too — a stale value
                              // would fail the match check on a field the user
                              // can no longer edit.
                              if (event.target.value === '') {
                                form.setFieldValue('confirmPassword', '');
                              }
                            }}
                            aria-invalid={field.state.meta.errors.length > 0}
                          />
                          <FieldError errors={field.state.meta.errors} />
                        </Field>
                      )}
                    </form.Field>

                    <form.Subscribe selector={(state) => state.values.newPassword === ''}>
                      {(passwordEmpty) => (
                        <form.Field name='confirmPassword'>
                          {(field) => (
                            <Field data-invalid={field.state.meta.errors.length > 0}>
                              <FieldLabel htmlFor={field.name}>
                                <Trans>Confirm password</Trans>
                              </FieldLabel>
                              <PasswordInput
                                id={field.name}
                                name={field.name}
                                autoComplete='new-password'
                                disabled={passwordEmpty}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(event) => field.handleChange(event.target.value)}
                                aria-invalid={field.state.meta.errors.length > 0}
                              />
                              <FieldError errors={field.state.meta.errors} />
                            </Field>
                          )}
                        </form.Field>
                      )}
                    </form.Subscribe>

                    <form.Field name='email'>
                      {(field) => (
                        <Field data-invalid={field.state.meta.errors.length > 0}>
                          <FieldLabel htmlFor={field.name}>
                            <Trans comment='Label of the email field on the user-creation page; the inner tag wraps the visually muted "(optional)" marker'>
                              Email{' '}
                              <span className='text-muted-foreground font-normal'>(optional)</span>
                            </Trans>
                          </FieldLabel>
                          <Input
                            id={field.name}
                            name={field.name}
                            type='email'
                            autoComplete='off'
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(event) => field.handleChange(event.target.value)}
                            aria-invalid={field.state.meta.errors.length > 0}
                          />
                          <FieldError errors={field.state.meta.errors} />
                        </Field>
                      )}
                    </form.Field>

                    <div className='flex items-center gap-2'>
                      <Switch
                        id='no-expiration'
                        checked={noExpiration}
                        onCheckedChange={setNoExpiration}
                      />
                      <Label htmlFor='no-expiration'>
                        <Trans>No expiration</Trans>
                      </Label>
                    </div>

                    <form.Field name='expiresOn'>
                      {(field) => (
                        <Field data-invalid={field.state.meta.errors.length > 0}>
                          <FieldLabel htmlFor={field.name}>
                            <Trans>Expiration date</Trans>
                          </FieldLabel>
                          <DatePicker
                            id={field.name}
                            disabled={noExpiration}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={field.handleChange}
                            aria-invalid={field.state.meta.errors.length > 0}
                            // Expiration must be a future day: tomorrow is the first
                            // selectable date (today itself is excluded).
                            startDate={toDateValue(addDays(new Date(), 1))}
                          />
                          <FieldError errors={field.state.meta.errors} />
                        </Field>
                      )}
                    </form.Field>
                  </FieldGroup>
                </CardContent>
              </Card>

              {hasRfid ?
                <Card>
                  <CardHeader>
                    <CardTitle className='flex items-center gap-2'>
                      <Nfc className='size-5' />
                      <Trans comment='Title of the card on the user edit pages listing the RFID badges assigned to the user, with controls to add and remove them.'>
                        RFID cards
                      </Trans>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='flex flex-col gap-4'>
                    {rfidUids.length === 0 ?
                      <p className='text-muted-foreground text-center text-sm'>
                        <Trans>No RFID cards yet.</Trans>
                      </p>
                    : <ul className='divide-y rounded-md border'>
                        {rfidUids.map((uid) => (
                          <li
                            key={uid}
                            className='flex items-center justify-between gap-3 px-3 py-2'
                          >
                            <p className='font-mono text-sm'>{uid}</p>
                            <Button
                              type='button'
                              variant='destructive'
                              size='sm'
                              onClick={() =>
                                setRfidUids(rfidUids.filter((listed) => listed !== uid))
                              }
                            >
                              <Trans>Remove</Trans>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    }

                    <Field data-invalid={rfidDraftError !== null}>
                      <FieldLabel htmlFor='rfid-uid'>
                        <Trans>Card UID</Trans>
                      </FieldLabel>
                      <div className='flex items-start gap-2'>
                        <Input
                          id='rfid-uid'
                          value={rfidDraft}
                          onChange={(event) => handleRfidDraftChange(event.target.value)}
                          // The whole page is one form: Enter in this field must
                          // add the card to the list, not create the user.
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleAddRfid();
                            }
                          }}
                          maxLength={16}
                          className='font-mono uppercase'
                          placeholder={t`e.g. A1B2C3`}
                          aria-invalid={rfidDraftError !== null}
                        />
                        <Button type='button' onClick={handleAddRfid}>
                          <Trans>Add</Trans>
                        </Button>
                      </div>
                      {rfidDraftError !== null ?
                        <FieldError>{rfidDraftError}</FieldError>
                      : null}
                    </Field>
                  </CardContent>
                </Card>
              : null}

              {hasNrgkick ?
                <Card>
                  <CardHeader>
                    <CardTitle className='flex items-center gap-2'>
                      <Zap className='size-5' />
                      <Trans comment='Title of the card on the user edit pages holding the NRGKick charger settings (enable switch, device serial, energy price). NRGKick is a product name — do not translate it.'>
                        NRGKick
                      </Trans>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <FieldGroup>
                      <div className='flex items-center gap-2'>
                        <Switch
                          id='nrgkick-enabled'
                          checked={nrgkickOn}
                          onCheckedChange={setNrgkickOn}
                        />
                        <Label htmlFor='nrgkick-enabled'>
                          <Trans>Enable NRGKick</Trans>
                        </Label>
                      </div>

                      {nrgkickOn ?
                        <>
                          <form.Field name='nrgkickDeviceSerial'>
                            {(field) => (
                              <Field data-invalid={field.state.meta.errors.length > 0}>
                                <FieldLabel htmlFor={field.name}>
                                  <Trans>Device serial</Trans>
                                </FieldLabel>
                                <Input
                                  id={field.name}
                                  name={field.name}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(event) => field.handleChange(event.target.value)}
                                  aria-invalid={field.state.meta.errors.length > 0}
                                />
                                <FieldError errors={field.state.meta.errors} />
                              </Field>
                            )}
                          </form.Field>

                          <form.Field name='nrgkickPricePerKwh'>
                            {(field) => (
                              <Field data-invalid={field.state.meta.errors.length > 0}>
                                <FieldLabel htmlFor={field.name}>
                                  <Trans>Energy cost (€/kWh)</Trans>
                                </FieldLabel>
                                <Input
                                  id={field.name}
                                  name={field.name}
                                  type='number'
                                  inputMode='decimal'
                                  step='0.01'
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(event) => field.handleChange(event.target.value)}
                                  aria-invalid={field.state.meta.errors.length > 0}
                                />
                                <FieldError errors={field.state.meta.errors} />
                              </Field>
                            )}
                          </form.Field>
                        </>
                      : null}
                    </FieldGroup>
                  </CardContent>
                </Card>
              : null}
            </div>

            <div className='mt-6 flex justify-end gap-2'>
              <Button type='button' variant='outline' onClick={resetForm}>
                <Trans>Reset</Trans>
              </Button>
              <form.Subscribe selector={(state) => state.isSubmitting}>
                {(isSubmitting) => (
                  <Button type='submit' disabled={isSubmitting}>
                    {isSubmitting ? t`Saving…` : t`Save`}
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </form>
        </>
      : <CredentialsCard
          credentials={credentials}
          failures={failures}
          onCreateAnother={() => {
            setCredentials(null);
            setFailures(NO_FAILURES);
          }}
        />
      }
    </PageContainer>
  );
}

/**
 * The in-place replacement for the form after a successful create (the nested
 * credentials dialog is gone, matching the fast-create swap): the printable
 * generated credentials, prefixed by a warning block when any chained
 * follow-up failed — the account exists and the password is only available
 * here, so failures inform rather than block, and "Go to user details" is the
 * fix path.
 */
function CredentialsCard({
  credentials,
  failures,
  onCreateAnother,
}: {
  credentials: APICreatedUser;
  failures: FollowUpFailures;
  onCreateAnother: () => void;
}) {
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);
  const printCredentials = useReactToPrint({
    contentRef: printRef,
    pageStyle: '@page { size: auto; margin: 0; }',
  });
  const hasFailures = failures.nrgkick || failures.rfids.length > 0;

  return (
    <Card className='max-w-xl'>
      <CardHeader>
        <CardTitle>
          <Trans>New user credentials</Trans>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasFailures ?
          <Alert>
            <TriangleAlert />
            <AlertTitle>
              <Trans comment='Title of the warning shown with a newly created user’s credentials when a chained follow-up call (NRGKick enable / RFID assignment) failed after the account itself was created'>
                The user was created, but some settings could not be applied.
              </Trans>
            </AlertTitle>
            <AlertDescription>
              <ul className='ml-4 list-disc'>
                {failures.nrgkick ?
                  <li>
                    <Trans comment='Warning list item after user creation. NRGKick is a product name — do not translate it.'>
                      NRGKick could not be enabled.
                    </Trans>
                  </li>
                : null}
                {failures.rfids.map(({ uid, conflictName }) => (
                  <li key={uid}>
                    {conflictName !== null ?
                      <Trans comment='Warning list item after user creation; {uid} is the RFID card UID, {conflictName} the username of the user the card already belongs to'>
                        Card {uid} is already assigned to {conflictName}.
                      </Trans>
                    : <Trans comment='Warning list item after user creation; {uid} is the RFID card UID'>
                        Card {uid} could not be assigned.
                      </Trans>
                    }
                  </li>
                ))}
              </ul>
              <p className='mt-1'>
                <Trans comment='Hint closing the warning about failed follow-up settings after user creation; the "Go to user details" button below leads to the page where they can be fixed'>
                  You can fix this from the user's edit page.
                </Trans>
              </p>
            </AlertDescription>
          </Alert>
        : null}

        <div ref={printRef} className='py-4 print:p-[16mm]'>
          <div className='space-y-1 text-sm print:w-fit print:space-y-3 print:rounded-xl print:border print:p-8'>
            <p className='hidden text-lg font-semibold print:block'>
              <Trans>New user credentials</Trans>
            </p>
            <p className='text-muted-foreground hidden pb-4 print:block'>{SITE_NAME}</p>
            <p>
              <span className='text-muted-foreground'>
                <Trans>Username:</Trans>
              </span>{' '}
              <span className='font-medium'>{credentials.username}</span>
            </p>
            <p>
              <span className='text-muted-foreground'>
                <Trans>Password:</Trans>
              </span>{' '}
              <span className='font-medium'>{credentials.password}</span>
            </p>
          </div>
        </div>

        <div className='flex flex-wrap justify-end gap-2'>
          <Button variant='outline' onClick={() => printCredentials()}>
            <Trans>Print</Trans>
          </Button>
          <Button variant='outline' onClick={onCreateAnother}>
            <Trans comment='Button shown after a user account is successfully created; dismisses the credentials and resets the form to create another user'>
              Create another
            </Trans>
          </Button>
          <Button variant='outline' onClick={() => void navigate({ to: '/users' })}>
            <Trans>Back to users</Trans>
          </Button>
          <Button
            onClick={() =>
              void navigate({
                to: '/users/$id',
                params: { id: String(credentials.id) },
              })
            }
          >
            <Trans>Go to user details</Trans>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
